import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, ButtonBase, Grid, Typography } from "@mui/material";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxMenu, { AcxMenuItemProps } from "components/UI/Menu/AcxMenu";
import { autorun } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { useLocation } from "react-router-dom";
import { useStore } from "utils/useStore";
import { useResizeObserver } from "../../../utils/useResizeObserver";
import { buildReportVizType } from "./Common/ReportVisualizationBuilder";
import ReportCardFilters from "./ReportCards/ReportCardFilters";
import ReportHeader from "./ReportHeader";
import theme from "Theme/AppTheme";
import ReportsStore from "./Stores/ReportsStore";
import LandingDashboard from "components/Dashboard/Landing/LandingDashboardSignals";

type StyleProps = {
    height?: number;
    width?: number;
    hideTitle?: boolean;
    top?: number;
    right?: number;
};

const SignalsCustomReportBody = observer((props: StyleProps) => {
    const reportsStore = useStore(ReportsStore);

    const location = useLocation();

    function shouldBlockWidgetRemoveDrag(target: EventTarget | null) {
        if (!(target instanceof Element)) {
            return false;
        }

        const button = target.closest("button");
        if (!button) {
            return false;
        }

        return Boolean(button.querySelector('svg[data-testid="CloseIcon"]'));
    }

    function handleReportingDashboardMouseDownCapture(
        event: React.MouseEvent<HTMLDivElement>,
    ) {
        if (shouldBlockWidgetRemoveDrag(event.target)) {
            event.stopPropagation();
        }
    }

    function handleReportingDashboardTouchStartCapture(
        event: React.TouchEvent<HTMLDivElement>,
    ) {
        if (shouldBlockWidgetRemoveDrag(event.target)) {
            event.stopPropagation();
        }
    }

    React.useEffect(() => {
        const getOrgIdLocation = () => {
            const pathArray = location.pathname.split("/");
            const groupIndex =
                pathArray.findIndex(
                    (value) => value === "reporting" || value === "reports",
                ) + 1;
            return pathArray[groupIndex];
        };
        const getReportId = () => {
            const pathArray = location.pathname.split("/");
            const groupIndex =
                pathArray.findIndex(
                    (value) => value === "reporting" || value === "reports",
                ) + 2;
            return pathArray[groupIndex];
        };

        const orgId = getOrgIdLocation();
        const reportId = getReportId();
        const curRpt = reportsStore.reports.find(
            (r) => r?.organizationId === orgId && r.id === reportId,
        );

        if (curRpt === undefined) {
            reportsStore.currentReport = null;
            reportsStore.clearDrillDownState();
            reportsStore.prepareReportSwitch();
        }

        if (
            curRpt !== undefined &&
            curRpt.id !== reportsStore.currentReport?.id
        ) {
            reportsStore.clearDrillDownState();
            reportsStore.prepareReportSwitch();

            reportsStore.currentReport = curRpt;
        }

        reportsStore.currentGroup = orgId;
    }, [
        location.pathname,
        location,
        reportsStore.reports.length,
        reportsStore.navStore.currentNavItem,
        reportsStore.currentReport,
        reportsStore,
        reportsStore.orgStore.selectedOrganization,
    ]);

    const [menuItems, setMenuItems] = React.useState<
        AcxMenuItemProps[] | undefined
    >();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { elementHeight, elementWidth, theRef } =
        useResizeObserver<HTMLDivElement>({
            debounceWait: 33,
        });

    const {
        elementHeight: controlsHeight,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        elementWidth: controlsWidth,
        theRef: controlsRef,
    } = useResizeObserver<HTMLDivElement>({
        debounceWait: 33,
    });

    function handleMoreMenuClick(event: React.MouseEvent<HTMLButtonElement>) {
        setAnchorEl(event.currentTarget);
        event.stopPropagation();
        event.preventDefault();
    }

    function handleMoreMenuClose() {
        setAnchorEl(null);
    }

    React.useEffect(() => {
        const disposer = autorun((r) => {
            const acxMenuItems = reportsStore?.menuItemsBuilder?.(() => {
                setAnchorEl(null);
            });

            setMenuItems(acxMenuItems);
        });

        return () => disposer();
    }, [reportsStore, reportsStore?.menuItemsBuilder]);

    return reportsStore.currentReport ? (
        <Grid item xs sx={{ width: "100%", height: "100%" }}>
            <ReportHeader
                domain={"Signals"}
                reportName={reportsStore.currentReport?.name}
                editedAt={reportsStore.currentReport?.modifiedAt ?? ""}
                editedBy={reportsStore.currentReport?.modifiedBy ?? ""}
                editedOn={reportsStore.currentReport?.modifiedOn ?? ""}
            />

            <Grid
                item
                xs
                sx={{ padding: theme.spacing(2), height: "calc(100% - 95px)" }}
            >
                <Grid
                    container
                    ref={theRef}
                    item
                    xs
                    sx={{
                        height: "100%",
                        padding: 2,
                        backgroundColor: theme.palette.white.main,
                        boxShadow: theme.shadows[1],
                        borderRadius: "5px",
                    }}
                >
                    <Grid
                        ref={controlsRef}
                        item
                        xs={12}
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems={"flex-start"}
                        wrap={"nowrap"}
                    >
                        {(!props.hideTitle ||
                            Boolean(
                                reportsStore.reportDataViewModel
                                    .drillDownForCurrentReport,
                            )) && (
                            <Grid item xs={1} container alignItems={"baseline"}>
                                {!props.hideTitle && (
                                    <Grid item>
                                        <Typography variant="h5">
                                            {reportsStore.currentReport?.name}
                                        </Typography>
                                    </Grid>
                                )}

                                {Boolean(
                                    reportsStore.reportDataViewModel
                                        .drillDownForCurrentReport,
                                ) && (
                                    <Grid item>
                                        <ButtonBase
                                            onClick={
                                                reportsStore.clearDrillDownState
                                            }
                                            sx={{
                                                width: "32px",
                                                height: "32px",
                                                color: theme.palette.black.main,
                                            }}
                                        >
                                            <KeyboardReturnIcon />
                                        </ButtonBase>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                        {(!props.hideTitle ||
                            Boolean(
                                reportsStore.reportDataViewModel
                                    .drillDownForCurrentReport,
                            )) && (
                            <Grid
                                item
                                xs={11}
                                container
                                style={{ marginLeft: "-5%" }}
                            >
                                <ReportCardFilters />
                            </Grid>
                        )}
                        {props.hideTitle &&
                            !Boolean(
                                reportsStore.reportDataViewModel
                                    .drillDownForCurrentReport,
                            ) && <ReportCardFilters />}

                        {menuItems &&
                            !reportsStore.reportDataViewModel.isDrillDown && (
                                <Grid item style={{ position: "relative" }}>
                                    <ButtonBase
                                        onClick={handleMoreMenuClick}
                                        sx={{
                                            width: "48px",
                                            height: "32px",
                                            top:
                                                props.top !== undefined
                                                    ? props.top
                                                    : -9,
                                            right:
                                                props.right !== undefined
                                                    ? props.right
                                                    : -9,
                                        }}
                                        disabled={
                                            reportsStore.reportDataViewModel
                                                .anyTaskLoading
                                        }
                                    >
                                        <MoreVertIcon />
                                    </ButtonBase>
                                </Grid>
                            )}

                        {menuItems &&
                            !reportsStore.reportDataViewModel.isDrillDown && (
                                <AcxMenu
                                    menuItems={menuItems}
                                    anchorElement={anchorEl}
                                    onMenuClose={handleMoreMenuClose}
                                    anchorOrigin={{
                                        vertical: "top",
                                        horizontal: "right",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "left",
                                    }}
                                />
                            )}
                    </Grid>

                    <Grid
                        container
                        item
                        xs={12}
                        style={{
                            height: "calc(100% - 48px)",
                        }}
                    >
                        <div
                            style={{
                                height: `${
                                    (elementHeight ?? 0) -
                                    ((controlsHeight ?? 0) + 15)
                                }px`,
                                width: `${elementWidth ?? 0}px`,
                            }}
                        >
                            {reportsStore.isSettingUpReports ||
                            reportsStore.reportDataViewModel.getTaskLoadingV2(
                                "Loading Report",
                            ) ||
                            reportsStore.reportDataViewModel.getTaskLoadingV2(
                                "Load Drill Down",
                            ) ? (
                                <AcxLoadingIndicator
                                    alternate="PuffLoader"
                                    size={64}
                                />
                            ) : (
                                buildReportVizType(
                                    reportsStore.reportDataViewModel
                                        .currentReport?.vizType,
                                    reportsStore.reportDataViewModel
                                        .emptyDataSet,
                                    reportsStore.reportDataViewModel
                                        .googleFormattedData,
                                    reportsStore.reportDataViewModel.isDrillDown
                                        ? reportsStore.reportDataViewModel
                                              .drillReportData
                                        : reportsStore.reportDataViewModel
                                              .tableReportData,
                                    reportsStore.reportDataViewModel
                                        .currentReport?.xAxisLabel,
                                    reportsStore.reportDataViewModel
                                        .currentReport?.yAxisLabel,
                                    reportsStore.reportDataViewModel
                                        .isDrillDown,
                                    reportsStore.reportDataViewModel.onVizClick,
                                    elementHeight,
                                    controlsHeight,
                                    false,
                                    {
                                        ...reportsStore.currentReport
                                            ?.vizOptions,
                                    },
                                    reportsStore.captureChartRef,
                                )
                            )}
                        </div>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    ) : (
        <>
            <Grid
                container
                item
                xs={12}
                padding={theme.spacing(3)}
                paddingBottom={0}
            >
                <Grid item xs={12}>
                    <Box fontSize={"24px"} fontWeight={"bold"}>
                        Evaluation Reporting
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box fontSize={"14px"} fontWeight={400}>
                        Use this dashboard to report on all conversations with
                        an Evaluation.
                    </Box>
                </Grid>
            </Grid>
            <div
                onMouseDownCapture={handleReportingDashboardMouseDownCapture}
                onTouchStartCapture={handleReportingDashboardTouchStartCapture}
                style={{ height: "100%", width: "100%" }}
            >
                <LandingDashboard isSignals />
            </div>
        </>
    );
});

export default SignalsCustomReportBody;
